import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import { debounce } from 'lodash';

import classes from "./SearchFilters.module.css";
import Modal from "../UI/Modal"
import { getStudioLowestPrice } from "../../util/studioPricing";
import { studioHasTagValue } from "../../util/tagsHelper";

// Default values for URL state
const DEFAULT_URL_STATE = {
  activity: '',
  location: '',
  guests: null,
  minPrice: null,
  maxPrice: null,
  tags: [],
  groupSize: '',
  creatives: [],
  bookingOptions: [],
  activeOption: 'Alle',
  page: 1
};

export const Content = ({ 
  onClose, 
  urlState = DEFAULT_URL_STATE,
  onFilterUpdate,
  onReset,
  allStudios = [],
  // Legacy props for backward compatibility
  // activeOption,
  // setFilteredStudios,
  // fromBottomPage,
  // setActiveOption,
  // studioLocations,
  // updateFilteredLocations
}) => {
    
  const options = [
    { label: "Bis 5 Personen", value: "5" },
    { label: "Bis 10 Personen", value: "10" },
    { label: "Bis 20 Personen", value: "20" },
    { label: "Bis 40 Personen", value: "40" },
    { label: "Bis 50 Personen", value: "50" },
    { label: "Mehr als 50 Personen", value: "moreThan50" },
  ];

  const options2 = [
    { label: "Musikproduzent", label2: "Nur Studios mit Musikproduzenten", value: "Musik" },
    { label: "Fotograf", label2: "Nur Studios mit Fotografen", value: "Foto" },
    { label: "Videograf", label2: "Nur Studios mit Videografen", value: "Video" },
  ];
  
  const options3 = [
    { label: "Sofortbuchung", label2: "Nur Studios mit Sofortbuchung anzeigen.", value: "Musik" },
    { label: "Eigenständiger Check-In", label2: "Eigenständiger Zugang zum Studio", value: "Foto" },
    { label: "Flexiblere Stornierung", label2: "Nur Unterkünfte mit flexibler oder moderater Stornierung", value: "Video" },
  ];

  // Ensure urlState has all required properties
  const safeUrlState = useMemo(() => ({
    ...DEFAULT_URL_STATE,
    ...urlState
  }), [urlState]);

  // Local state for preview (before applying)
  const [localState, setLocalState] = useState(safeUrlState);
  const [previewCount, setPreviewCount] = useState(allStudios.length);
  const [isCalculating, setIsCalculating] = useState(false);

  // Fixed price range: 0 to 3000+ euros
  const FIXED_MIN_PRICE = 0;
  const FIXED_MAX_PRICE = 3000;

  // Price histogram calculation - IMPROVED
  const priceHistogram = useMemo(() => {
    
    if (!allStudios.length) {
      return {
        histogram: Array(50).fill(0),
        minPrice: FIXED_MIN_PRICE,
        maxPrice: FIXED_MAX_PRICE,
        rangeSize: Math.ceil((FIXED_MAX_PRICE - FIXED_MIN_PRICE) / 50)
      };
    }

    const priceCount = {};
    // COMMENTED OUT: Dynamic maxPrice calculation from studios
    // let minPrice = Infinity;
    // let maxPrice = -Infinity;

    // allStudios.forEach(studio => {
    //   const price = Math.round(studio.pricePerHour || 0);
    //   priceCount[price] = (priceCount[price] || 0) + 1;
    //   minPrice = Math.min(minPrice, price);
    //   maxPrice = Math.max(maxPrice, price);
    // });

    // // Handle edge case where all studios have the same price
    // if (minPrice === maxPrice) {
    //   minPrice = Math.max(0, minPrice - 10);
    //   maxPrice = maxPrice + 10;
    // }

    // Use fixed price range instead
    const minPrice = FIXED_MIN_PRICE;
    const maxPrice = FIXED_MAX_PRICE;

    // Still calculate histogram for visualization purposes
    allStudios.forEach(studio => {
      const studioPrice = getStudioLowestPrice(studio);
      if (studioPrice !== null) {
        const price = Math.round(studioPrice);
        priceCount[price] = (priceCount[price] || 0) + 1;
      }
    });

    const numBars = 50;
    const rangeSize = Math.ceil((maxPrice - minPrice) / numBars);
    const histogram = Array(numBars).fill(0);

    for (let price = minPrice; price <= maxPrice; price++) {
      const index = Math.min(Math.floor((price - minPrice) / rangeSize), numBars - 1);
      histogram[index] += priceCount[price] || 0;
    }

    return { histogram, minPrice, maxPrice, rangeSize };
  }, [allStudios]); // Remove fromBottomPage dependency

  // Update local state when urlState changes
  useEffect(() => {
    setLocalState(safeUrlState);
  }, [safeUrlState]);

  // UPDATE: Initialize price bounds when priceHistogram changes (only if not set)
  useEffect(() => {
    setLocalState(prevState => {
      const newState = { ...prevState };
      
      // Only set initial values if they are null (not set by user)
      if (prevState.minPrice === null || prevState.minPrice === DEFAULT_URL_STATE.minPrice) {
        newState.minPrice = FIXED_MIN_PRICE; // Use fixed min price
      }
      
      if (prevState.maxPrice === null || prevState.maxPrice === DEFAULT_URL_STATE.maxPrice) {
        newState.maxPrice = FIXED_MAX_PRICE; // Use fixed max price
      }

      return newState;
    });
  }, [priceHistogram]);

  // Calculate preview count when local state changes
  const calculatePreviewCount = useCallback(
    debounce((state) => {
      if (!allStudios.length || !state) {
        setPreviewCount(0);
        setIsCalculating(false);
        return;
      }

      setIsCalculating(true);
      
      try {
        // Apply filters to calculate preview count
        let filtered = allStudios.filter(studio => {
          // Price filter with safe defaults
          const studioPrice = getStudioLowestPrice(studio);
          const minPrice = state.minPrice ?? FIXED_MIN_PRICE;
          const maxPrice = state.maxPrice ?? FIXED_MAX_PRICE;
          
          return studioPrice !== null && studioPrice >= minPrice && studioPrice <= maxPrice;
        });

        // Group size filter
        if (state.groupSize) {
          filtered = filtered.filter(studio => studio.groupSize === state.groupSize);
        }

        // Creatives filter
        if (state.creatives && state.creatives.length > 0) {
          filtered = filtered.filter(studio => 
            state.creatives.some(creative => studio.creatives?.includes(creative))
          );
        }

        // Booking options filter
        if (state.bookingOptions && state.bookingOptions.length > 0) {
          filtered = filtered.filter(studio => 
            state.bookingOptions.some(option => studio.bookingOptions?.includes(option))
          );
        }

        // Active option filter
        if (state.activeOption && state.activeOption !== 'Alle') {
          filtered = filtered.filter(studio => studio.studioType === state.activeOption);
        }

        // Tags filter
        if (state.tags && state.tags.length > 0) {
          filtered = filtered.filter(studio => {
            return state.tags.some(selectedTag => 
              studioHasTagValue(studio, selectedTag)
            );
          });
        }

        setPreviewCount(filtered.length);
      } catch (error) {
        console.error('Error calculating preview count:', error);
        setPreviewCount(0);
      } finally {
        setIsCalculating(false);
      }
    }, 200),
    [allStudios]
  );

  useEffect(() => {
    calculatePreviewCount(localState);
  }, [localState, calculatePreviewCount]);

  // Handle local state updates
  const updateLocalState = useCallback((updates) => {
    setLocalState(prev => ({ ...prev, ...updates }));
  }, []);

  // Apply filters and close
  const handleApplyFilters = useCallback(() => {
    if (onFilterUpdate) {
      onFilterUpdate(localState);
    }
    onClose();
  }, [localState, onFilterUpdate, onClose]);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    if (onReset) {
      onReset();
    }
    onClose();
  }, [onReset, onClose]);

  // Updated price change handlers - allow full range of studio prices
  const handleMinPriceChange = useCallback((event) => {
    const value = Number(event.target.value);
    // Ensure min price doesn't exceed max price minus 1
    const newMinPrice = Math.min(value, (localState.maxPrice || FIXED_MAX_PRICE) - 1);
    updateLocalState({ minPrice: newMinPrice });
  }, [localState.maxPrice, updateLocalState]);
  
  const handleMaxPriceChange = useCallback((event) => {
    const value = Number(event.target.value);
    // Ensure max price doesn't go below min price plus 1
    const newMaxPrice = Math.max(value, (localState.minPrice || FIXED_MIN_PRICE) + 1);
    updateLocalState({ maxPrice: newMaxPrice });
  }, [localState.minPrice, updateLocalState]);

  // Group size change handler
  const handleGroupSizeChange = useCallback((value) => {
    const newGroupSize = localState.groupSize === value ? '' : value;
    updateLocalState({ groupSize: newGroupSize });
  }, [localState.groupSize, updateLocalState]);

  // Creatives change handler
  const handleCreativesChange = useCallback((value) => {
    const currentCreatives = localState.creatives || [];
    const newCreatives = currentCreatives.includes(value)
      ? currentCreatives.filter(item => item !== value)
      : [...currentCreatives, value];
    
    updateLocalState({ creatives: newCreatives });
  }, [localState.creatives, updateLocalState]);

  // Booking options change handler
  const handleBookingOptionsChange = useCallback((value) => {
    const currentOptions = localState.bookingOptions || [];
    const newOptions = currentOptions.includes(value)
      ? currentOptions.filter(item => item !== value)
      : [...currentOptions, value];
    
    updateLocalState({ bookingOptions: newOptions });
  }, [localState.bookingOptions, updateLocalState]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <h1>Filter</h1>
        <div onClick={onClose} className={classes.closeContainer2}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M8.12275 9.20703L0.613281 1.69757L1.60382 0.707031L9.11328 8.2165L16.6227 0.707031L17.6133 1.69757L10.1038 9.20703L17.6133 16.7165L16.6227 17.707L9.11328 10.1976L1.60382 17.707L0.613281 16.7165L8.12275 9.20703Z" fill="#1F1F1F"/>
          </svg>
        </div>
      </header>
      
      <div className={classes.filterContainer}>
        <div className={classes.horizontalLine}></div>
        
        {/* Price Range Slider - Fixed range: 0 to 3000+ euros */}
        <div className={classes.sliderContainer}> 
          <div className={classes.rangeSlider}>
            <input
              type="range"
              min={FIXED_MIN_PRICE}
              max={FIXED_MAX_PRICE}
              value={localState.minPrice || FIXED_MIN_PRICE}
              onChange={handleMinPriceChange}
              className={`${classes.thumb} ${classes.thumbLeft}`}
              style={{ zIndex: (localState.minPrice || FIXED_MIN_PRICE) > (localState.maxPrice || FIXED_MAX_PRICE) - 10 ? 5 : 3, left: '-10px' }}
            />
            <input
              type="range"
              min={FIXED_MIN_PRICE}
              max={FIXED_MAX_PRICE}
              value={localState.maxPrice || FIXED_MAX_PRICE}
              onChange={handleMaxPriceChange}
              className={`${classes.thumb} ${classes.thumbRight}`}
              style={{ zIndex: (localState.maxPrice || FIXED_MAX_PRICE) < (localState.minPrice || FIXED_MIN_PRICE) + 10 ? 5 : 4, left: '6px' }}
            />
            <div className={classes.slider}>
              <div className={classes.track}></div>
              <div
                className={classes.range}
                style={{
                  left: `${(((localState.minPrice || FIXED_MIN_PRICE) - FIXED_MIN_PRICE) / (FIXED_MAX_PRICE - FIXED_MIN_PRICE)) * 100}%`,
                  right: `${100 - (((localState.maxPrice || FIXED_MAX_PRICE) - FIXED_MIN_PRICE) / (FIXED_MAX_PRICE - FIXED_MIN_PRICE)) * 100}%`,
                }}
              ></div>
            </div>
          </div>
          <div className={classes.priceOutput}>
            <span>{localState.minPrice || FIXED_MIN_PRICE}€</span>
            <span>{localState.maxPrice || FIXED_MAX_PRICE}€+</span>
          </div>
        </div>
        
        {/* <div className={classes.horizontalLine}></div> */}
        
        {/* Group Size Filter */}
        {/* <div className={classes.peopleContainer}>
          <h3 className="titleSemibold3">Gruppengröße</h3>
          <span style={{color: "var(--dark-grey-4)"}} className="regularMiddle">Personen erlaubt im Studio</span>
          <div className={classes.peopleOptions}>
            {options.map((option) => (
              <div key={option.value} className={classes.option}>
                <input
                  className={classes.inputs}
                  type="checkbox"
                  id={option.value}
                  name="groupSize"
                  value={option.value}
                  checked={localState.groupSize === option.value}
                  onChange={() => handleGroupSizeChange(option.value)} 
                />
                <label className={classes.label} htmlFor={option.value}>{option.label}</label>
              </div>
            ))}
          </div>
        </div> */}
        
        {/* Uncomment these sections when needed */}
        {/* 
        <div className={classes.horizontalLine}></div>
        <div className={classes.peopleContainer}>
          <h3 className="titleSemibold3">Studios mit Kreativen</h3>
          <span style={{color: "var(--dark-grey-4)"}} className="regularMiddle">Studios mit kreativer Unterstützung</span>
          <div className={classes.studiosOptions}>
            {options2.map((option) => (
              <div key={option.value} className={classes.option}>
                <input
                  className={classes.inputs}
                  type="checkbox"
                  id={option.value}
                  name="creatives"
                  value={option.value}
                  checked={(localState.creatives || []).includes(option.value)}
                  onChange={() => handleCreativesChange(option.value)}
                />
                <div className={classes.containerLabels}>
                  <label className={classes.label} htmlFor={option.value}>{option.label}</label>
                  <label className={classes.labelSmall} htmlFor={option.value}>{option.label2}</label>
                </div>
              </div>
            ))}
          </div>
        </div>
        */}
      </div>
      
      <div className={classes.footer}>
        <span onClick={handleResetFilters} className="regularMiddle">Zurücksetzen</span>
        <button 
          onClick={handleApplyFilters} 
          className="regularMiddle"
          disabled={isCalculating}
        >
          {isCalculating 
            ? "Wird berechnet..." 
            : `${previewCount} Studios zeigen`}
        </button>
      </div>
    </div>
  );
};

export const SearchFiltersPhone = ({ open, onClose, activeOption }) => {
  return (
    <Modal open={open}>
      <Content onClose={onClose} activeOption={activeOption} />
    </Modal>
  );
};

export const SearchFilters = ({  
  open, 
  onClose, 
  urlState,
  onFilterUpdate,
  onReset,
  allStudios,
  // Legacy props for backward compatibility
  activeOption, 
  setFilteredStudios, 
  setActiveOption,
  fromBottomPage,
  studioLocations,
  updateFilteredLocations
}) => { 
  
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };
  
  return (
    <Modal 
      className="modalSearchFilter" 
      open={open} 
      onBackdropClick={handleBackdropClick}
    >
      <Content 
        onClose={onClose}
        urlState={urlState}
        onFilterUpdate={onFilterUpdate}
        onReset={onReset}
        allStudios={allStudios}
        fromBottomPage={fromBottomPage}
        // Legacy props
        activeOption={activeOption}
        setFilteredStudios={setFilteredStudios}
        setActiveOption={setActiveOption}
        studioLocations={studioLocations}
        updateFilteredLocations={updateFilteredLocations}
      />   
    </Modal>
  );
};
